import { isOutboundPacketActiveDraft } from './outboundPacketSchemas'
import type { components } from '@joyful/openapi/types.gen'

type OutboundPacketType = components['schemas']['OutboundPacketType']
type OutboundPacketStatus = components['schemas']['OutboundPacketStatus']
type BillerActionType = components['schemas']['BillerActionRead']['action_type']

export type OutboundPacketBillerActionPath =
  | 'draft-appeal'
  | 'submit-paper-claim'

export function getOutboundPacketBillerActionPath(
  packetType: OutboundPacketType,
): OutboundPacketBillerActionPath | null {
  if (packetType === 'APPEAL_PACKET') {
    return 'draft-appeal'
  }

  if (packetType === 'PAPER_CLAIM') {
    return 'submit-paper-claim'
  }

  return null
}

export function getOutboundPacketTypeForBillerActionPath(
  action: OutboundPacketBillerActionPath,
): OutboundPacketType {
  if (action === 'draft-appeal') {
    return 'APPEAL_PACKET'
  }

  return 'PAPER_CLAIM'
}

export function isOutboundPacketBillerActionPath(
  action: string,
): action is OutboundPacketBillerActionPath {
  return action === 'draft-appeal' || action === 'submit-paper-claim'
}

export function isOutboundPacketOwnedByBillerAction(
  action: string | null,
): boolean {
  return action !== null && isOutboundPacketBillerActionPath(action)
}

export function readOutboundPacketTypeFromBillerAction(action: {
  action_data?: components['schemas']['BillerActionRead']['action_data']
}): OutboundPacketType {
  const actionData = action.action_data
  if (
    !actionData ||
    typeof actionData !== 'object' ||
    Array.isArray(actionData)
  ) {
    return 'APPEAL_PACKET'
  }

  if (
    'packetType' in actionData &&
    (actionData.packetType === 'APPEAL_PACKET' ||
      actionData.packetType === 'PAPER_CLAIM')
  ) {
    return actionData.packetType
  }

  if (
    'submissionType' in actionData &&
    actionData.submissionType === 'claim-resubmission'
  ) {
    return 'PAPER_CLAIM'
  }

  return 'APPEAL_PACKET'
}

export function getOutboundPacketEditorRouteQueryParams(packet: {
  id: string
  packet_type: OutboundPacketType
  status: OutboundPacketStatus
}): {
  action: OutboundPacketBillerActionPath | null
  packet_id: string
  sidebar?: 'action'
  tab?: 'appeal_packets'
} {
  if (isOutboundPacketActiveDraft(packet)) {
    const action = getOutboundPacketBillerActionPath(packet.packet_type)
    if (action) {
      return {
        action,
        packet_id: packet.id,
        sidebar: 'action',
      }
    }
  }

  return {
    action: null,
    packet_id: packet.id,
    tab: 'appeal_packets',
  }
}

export function getOutboundPacketEditorRouteQueryParamsForBillerAction({
  actionType,
  outboundPacketId,
  packetType,
}: {
  actionType: BillerActionType
  outboundPacketId: string
  packetType: OutboundPacketType
}) {
  return getOutboundPacketEditorRouteQueryParams({
    id: outboundPacketId,
    packet_type: packetType,
    status: actionType === 'SUBMITTED' ? 'FINALIZED' : 'NEEDS_REVIEW',
  })
}

export function getActionPathQueryParamsUpdate<TAction extends string | null>({
  nextAction,
  previousAction,
}: {
  nextAction: TAction
  previousAction: string | null
}): {
  action: TAction
  actionId: null
  intent: null
  packet_id?: null
} {
  const didActionChange = nextAction !== previousAction
  const isLeavingOutboundPacketAction =
    previousAction !== null && isOutboundPacketBillerActionPath(previousAction)
  const isEnteringNonOutboundPacketAction =
    nextAction !== null && !isOutboundPacketBillerActionPath(nextAction)
  const shouldClearPacketId =
    didActionChange &&
    (isLeavingOutboundPacketAction || isEnteringNonOutboundPacketAction)

  if (shouldClearPacketId) {
    return {
      action: nextAction,
      actionId: null,
      intent: null,
      packet_id: null,
    }
  }

  return {
    action: nextAction,
    actionId: null,
    intent: null,
  }
}
